import { Loader2, UploadCloud } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { UploadStatus } from "./use-arquivo-upload";

interface UploadProgressProps {
  /** Progresso do envio dos bytes (0–100). */
  percent: number;
  /** Estado do upload; em `finalizing`/`done` mostra "Finalizando…". */
  status?: UploadStatus;
  /** Título durante o envio (ex.: "Enviando documento…"). */
  title?: string;
  className?: string;
  /**
   * `block` (padrão): bloco central com ícone — substitui o spinner nos dialogs de
   * upload. `inline`: barra compacta (label + %) para caber acima de rodapés/botões
   * em dialogs de confirmação, sem quebrar o layout.
   */
  variant?: "block" | "inline";
}

/**
 * Barra de progresso reutilizável para os dialogs de upload. Substitui o spinner
 * "Enviando…" por uma barra com % real ("subindo… pode demorar"). Enquanto o backend
 * confirma / o dialog faz o trabalho pós-upload, exibe "Finalizando…".
 */
export function UploadProgress({
  percent,
  status = "uploading",
  title = "Enviando documento…",
  className,
  variant = "block",
}: Readonly<UploadProgressProps>) {
  const finalizing = status === "finalizing" || status === "done";

  if (variant === "inline") {
    return (
      <div className={cn("w-full space-y-1.5", className)}>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{finalizing ? "Finalizando…" : title}</span>
          {!finalizing && <span>{percent}%</span>}
        </div>
        <Progress value={finalizing ? 100 : percent} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center space-y-4 py-12 duration-300 animate-in fade-in zoom-in-95",
        className,
      )}
    >
      <div className="rounded-full bg-primary/10 p-4">
        {finalizing ? (
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        ) : (
          <UploadCloud className="h-8 w-8 text-primary" />
        )}
      </div>

      <div className="w-full max-w-[280px] space-y-2 text-center">
        <p className="text-sm font-semibold text-foreground">
          {finalizing ? "Finalizando…" : title}
        </p>
        <Progress value={finalizing ? 100 : percent} />
        <p className="text-xs text-muted-foreground">
          {finalizing
            ? "Quase lá, só um instante."
            : `${percent}% • arquivos grandes podem demorar`}
        </p>
      </div>
    </div>
  );
}
